import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  listTriageBatches,
  getTriageBatch,
  checkTriageItemStatus,
  exportTriageItem,
  updateTriageItem,
} from '../api/client';
import type { TriageQueueId } from '../types';

export function useTriageBatches(queue: TriageQueueId) {
  return useQuery({
    queryKey: ['triage-batches', queue],
    queryFn: () => listTriageBatches(queue),
    staleTime: 30 * 1000,
  });
}

export function useTriageBatch(queue: TriageQueueId, date: string) {
  return useQuery({
    queryKey: ['triage-batch', queue, date],
    queryFn: () => getTriageBatch(queue, date),
    staleTime: 10 * 1000,
    enabled: !!date,
  });
}

export function useCheckTriageItem(queue: TriageQueueId, date: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (itemId: string) => checkTriageItemStatus(queue, date, itemId),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['triage-batch', queue, date] });
      queryClient.invalidateQueries({ queryKey: ['triage-batches', queue] });
    },
  });
}

export function useExportTriageItem(queue: TriageQueueId, date: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (itemId: string) => exportTriageItem(queue, date, itemId),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['triage-batch', queue, date] });
      queryClient.invalidateQueries({ queryKey: ['triage-batches', queue] });
    },
  });
}

export function useUpdateTriageItem(queue: TriageQueueId, date: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ itemId, status, notes }: { itemId: string; status: 'pending' | 'resolved'; notes?: string }) =>
      updateTriageItem(queue, date, itemId, status, notes),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['triage-batch', queue, date] });
      queryClient.invalidateQueries({ queryKey: ['triage-batches', queue] });
    },
  });
}
